import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';
import { breakpoints } from '../../tokens/breakpoints';

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Types                                                                 */
/* ═══════════════════════════════════════════════════════════════════════ */

export type FileItemStatus = 'idle' | 'uploading' | 'success' | 'error';

export interface FileItemRootProps extends React.HTMLAttributes<HTMLDivElement> {
  status?: FileItemStatus;
  disabled?: boolean;
  semanticId?: string;
  children: React.ReactNode;
}

export interface FileItemIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Material Symbol name. Auto-derived from status if omitted. */
  name?: string;
  children?: React.ReactNode;
}

export interface FileItemNameProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

export interface FileItemMetaProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

export interface FileItemProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0–100 */
  value: number;
}

export interface FileItemActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export type FileItemActionIntent = 'default' | 'danger' | 'retry';

export interface FileItemActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Material Symbol name for the action icon. */
  icon: string;
  /** Visual intent controlling hover color. */
  intent?: FileItemActionIntent;
  /** Accessible label. */
  label?: string;
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Context                                                               */
/* ═══════════════════════════════════════════════════════════════════════ */

interface FileItemContextValue {
  status: FileItemStatus;
  disabled: boolean;
}

const FileItemContext = React.createContext<FileItemContextValue>({
  status: 'idle',
  disabled: false,
});

const useFileItemContext = () => React.useContext(FileItemContext);

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Styled                                                                */
/* ═══════════════════════════════════════════════════════════════════════ */

const TRANSIENT_ROOT = ['$status', '$disabled'] as const;
const shouldForwardRoot = (prop: string | number): boolean =>
  !TRANSIENT_ROOT.includes(prop as (typeof TRANSIENT_ROOT)[number]);

const StyledRoot = styled.div.withConfig({ shouldForwardProp: shouldForwardRoot })<{
  $status: FileItemStatus;
  $disabled: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${rem(8)};
  width: 100%;
  min-height: ${rem(56)};
  padding: ${rem(10)} ${rem(12)};
  border-radius: var(--ui-radius-md, ${rem(7)});
  background-color: var(--ui-background-muted, ${palette.OffWhite});
  font-family: var(--ui-font-sans);
  transition: background-color 150ms ease;

  @media (max-width: ${breakpoints.sm - 1}px) {
    padding: ${rem(10)} ${rem(10)};
    gap: ${rem(6)};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
    `}
`;

const StyledInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  min-width: 0;
  flex: 1;
`;

/* ── Icon ──────────────────────────────────────────────────────────────── */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const StyledIcon = styled.span.withConfig({
  shouldForwardProp: (prop) => String(prop) !== '$status',
})<{ $status: FileItemStatus }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(32)};
  height: ${rem(32)};

  .material-symbols-outlined {
    font-size: ${rem(22)};
  }

  ${({ $status }) => {
    switch ($status) {
      case 'success':
        return css`
          .material-symbols-outlined {
            color: var(--ui-status-success, ${palette.Green});
          }
        `;
      case 'error':
        return css`
          .material-symbols-outlined {
            color: var(--ui-status-danger, ${palette.Red});
          }
        `;
      case 'uploading':
        return css`
          .material-symbols-outlined {
            color: var(--ui-interactive, ${palette.Iris});
            animation: ${spin} 1.2s linear infinite;
          }
        `;
      case 'idle':
      default:
        return css`
          .material-symbols-outlined {
            color: var(--ui-text-subtle, ${palette.SpanishGray});
          }
        `;
    }
  }}
`;

const STATUS_ICON_MAP: Record<FileItemStatus, string> = {
  idle: 'draft',
  uploading: 'sync',
  success: 'check_circle',
  error: 'error',
};

/* ── Name / Meta ──────────────────────────────────────────────────────── */

const StyledName = styled.span`
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(18)};
  color: var(--ui-text, ${palette.Black});
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;

  @media (max-width: ${breakpoints.sm - 1}px) {
    font-size: ${rem(13)};
    white-space: normal;
    word-break: break-word;
  }
`;

const StyledMeta = styled.span`
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.sm - 1}px) {
    font-size: ${rem(11)};
    white-space: normal;
  }
`;

/* ── Progress ─────────────────────────────────────────────────────────── */

const StyledProgressTrack = styled.div`
  height: ${rem(4)};
  width: 100%;
  background-color: var(--ui-border-subtle, ${palette.CulturedWhite});
  border-radius: var(--ui-radius-sm, ${rem(4)});
  overflow: hidden;
`;

const StyledProgressBar = styled.div.withConfig({
  shouldForwardProp: (prop) => String(prop) !== '$value',
})<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => `${Math.min(100, Math.max(0, $value))}%`};
  background-color: var(--ui-interactive, ${palette.Iris});
  border-radius: var(--ui-radius-sm, ${rem(4)});
  transition: width 200ms ease;
`;

/* ── Actions ──────────────────────────────────────────────────────────── */

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  flex-shrink: 0;
`;

const TRANSIENT_ACTION = ['$intent'] as const;
const shouldForwardAction = (prop: string | number): boolean =>
  !TRANSIENT_ACTION.includes(prop as (typeof TRANSIENT_ACTION)[number]);

const StyledActionBtn = styled.button.withConfig({
  shouldForwardProp: shouldForwardAction,
})<{ $intent: FileItemActionIntent }>`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(4)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  transition: color 150ms ease;

  .material-symbols-outlined {
    font-size: ${rem(18)};
    color: var(--ui-text-subtle, ${palette.SpanishGray});
  }

  &:hover .material-symbols-outlined {
    ${({ $intent }) => {
      switch ($intent) {
        case 'danger':
          return css`
            color: var(--ui-status-danger, ${palette.Red});
          `;
        case 'retry':
          return css`
            color: var(--ui-interactive, ${palette.Iris});
          `;
        default:
          return css`
            color: var(--ui-text, ${palette.Black});
          `;
      }
    }}
  }

  &:focus-visible {
    outline: ${rem(2)} solid var(--ui-interactive, ${palette.Purple});
    outline-offset: ${rem(2)};
  }

  &:disabled {
    opacity: 0.4;
    pointer-events: none;
  }
`;

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Sub-components                                                        */
/* ═══════════════════════════════════════════════════════════════════════ */

const FileItemRoot = React.forwardRef<HTMLDivElement, FileItemRootProps>(
  function FileItemRoot(
    { status = 'idle', disabled = false, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const ctx = React.useMemo(
      () => ({ status, disabled }),
      [status, disabled],
    );

    return (
      <FileItemContext.Provider value={ctx}>
        <StyledRoot
          ref={ref}
          $status={status}
          $disabled={disabled}
          data-status={status}
          data-disabled={disabled || undefined}
          {...semanticProps}
          {...rest}
        >
          {children}
        </StyledRoot>
      </FileItemContext.Provider>
    );
  },
);
FileItemRoot.displayName = 'FileItem.Root';

const FileItemIcon = React.forwardRef<HTMLSpanElement, FileItemIconProps>(
  function FileItemIcon({ name, children, ...rest }, ref) {
    const { status } = useFileItemContext();
    const iconName = name ?? STATUS_ICON_MAP[status];

    return (
      <StyledIcon ref={ref} $status={status} {...rest}>
        {children ?? (
          <span className="material-symbols-outlined">{iconName}</span>
        )}
      </StyledIcon>
    );
  },
);
FileItemIcon.displayName = 'FileItem.Icon';

const FileItemName = React.forwardRef<HTMLSpanElement, FileItemNameProps>(
  function FileItemName({ children, ...rest }, ref) {
    return (
      <StyledName ref={ref} title={typeof children === 'string' ? children : undefined} {...rest}>
        {children}
      </StyledName>
    );
  },
);
FileItemName.displayName = 'FileItem.Name';

const FileItemMeta = React.forwardRef<HTMLSpanElement, FileItemMetaProps>(
  function FileItemMeta({ children, ...rest }, ref) {
    return (
      <StyledMeta ref={ref} {...rest}>
        {children}
      </StyledMeta>
    );
  },
);
FileItemMeta.displayName = 'FileItem.Meta';

const FileItemProgress = React.forwardRef<HTMLDivElement, FileItemProgressProps>(
  function FileItemProgress({ value, ...rest }, ref) {
    return (
      <StyledProgressTrack
        ref={ref}
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={100}
        {...rest}
      >
        <StyledProgressBar $value={value} />
      </StyledProgressTrack>
    );
  },
);
FileItemProgress.displayName = 'FileItem.Progress';

const FileItemActions = React.forwardRef<HTMLDivElement, FileItemActionsProps>(
  function FileItemActions({ children, ...rest }, ref) {
    return (
      <StyledActions ref={ref} {...rest}>
        {children}
      </StyledActions>
    );
  },
);
FileItemActions.displayName = 'FileItem.Actions';

const FileItemAction = React.forwardRef<HTMLButtonElement, FileItemActionProps>(
  function FileItemAction({ icon, intent = 'default', label, ...rest }, ref) {
    return (
      <StyledActionBtn
        ref={ref}
        type="button"
        $intent={intent}
        aria-label={label}
        title={label}
        {...rest}
      >
        <span className="material-symbols-outlined">{icon}</span>
      </StyledActionBtn>
    );
  },
);
FileItemAction.displayName = 'FileItem.Action';

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Convenience wrapper — pre-composed "simple" FileItem                  */
/* ═══════════════════════════════════════════════════════════════════════ */

/**
 * A pre-composed layout that wraps Root > (Icon + info column) + Actions.
 * For complex layouts use the compound sub-components directly.
 */
export interface FileItemSimpleProps extends Omit<FileItemRootProps, 'children'> {
  fileName: string;
  /** Secondary text (e.g. file size, date). */
  meta?: string;
  /** 0–100. Only rendered when status is 'uploading'. */
  progress?: number;
  /** Action buttons to display on the right. */
  actions?: React.ReactNode;
}

const FileItemSimple = React.forwardRef<HTMLDivElement, FileItemSimpleProps>(
  function FileItemSimple(
    { fileName, meta, progress, actions, status = 'idle', ...rootProps },
    ref,
  ) {
    return (
      <FileItemRoot ref={ref} status={status} {...rootProps}>
        <StyledInfo>
          <FileItemIcon />
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: rem(2) }}>
            <FileItemName>{fileName}</FileItemName>
            {status === 'uploading' && progress != null && (
              <FileItemProgress value={progress} />
            )}
            {meta && <FileItemMeta>{meta}</FileItemMeta>}
          </div>
        </StyledInfo>
        {actions && <FileItemActions>{actions}</FileItemActions>}
      </FileItemRoot>
    );
  },
);
FileItemSimple.displayName = 'FileItem';

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Public API                                                            */
/* ═══════════════════════════════════════════════════════════════════════ */

export const FileItem = Object.assign(FileItemSimple, {
  Root: FileItemRoot,
  Icon: FileItemIcon,
  Name: FileItemName,
  Meta: FileItemMeta,
  Progress: FileItemProgress,
  Actions: FileItemActions,
  Action: FileItemAction,
});
